import React, { useEffect, useState } from "react";
import api, { rupee, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Store, Plus, Pencil, Trash2, Save, X, Clock, MapPin } from "lucide-react";

const EMPTY = { name: "", address: "", pincode: "", phone: "", pickup_hours: "11:00 AM – 11:00 PM", ready_time: "20–30 mins", min_order: 199, active: true };

export default function AdminKitchens() {
  const [kitchens, setKitchens] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/kitchens").then((r) => setKitchens(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditingId(null); setShowForm(false); };
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.name || !form.address) { toast.error("Name and address are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, min_order: Number(form.min_order) || 0 };
      if (editingId) { await api.put(`/admin/kitchens/${editingId}`, payload); toast.success("Kitchen updated"); }
      else { await api.post("/admin/kitchens", payload); toast.success("Kitchen added"); }
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const edit = (k) => {
    setForm({ name: k.name || "", address: k.address || "", pincode: k.pincode || "", phone: k.phone || "",
      pickup_hours: k.pickup_hours || "", ready_time: k.ready_time || "", min_order: k.min_order ?? 199, active: !!k.active });
    setEditingId(k.id); setShowForm(true);
  };
  const remove = async (id) => { await api.delete(`/admin/kitchens/${id}`); toast.success("Kitchen removed"); load(); };
  const toggleActive = async (k) => {
    await api.put(`/admin/kitchens/${k.id}`, { ...k, min_order: Number(k.min_order) || 0, active: !k.active });
    load();
  };

  return (
    <div data-testid="admin-kitchens" className="max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-3xl text-navy flex items-center gap-2"><Store size={24} className="text-gold" /> Operating Kitchens</h1>
          <p className="text-sm text-muted-foreground mt-1">Only <b>active</b> kitchens appear as takeaway/pickup options at checkout.</p>
        </div>
        {!showForm && <button onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(true); }} data-testid="add-kitchen-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-navy-soft"><Plus size={16} /> Add Kitchen</button>}
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-black/5 p-6 mb-5" data-testid="kitchen-form">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-xl text-navy">{editingId ? "Edit Kitchen" : "Add Kitchen"}</h3>
            <button onClick={reset} className="text-muted-foreground hover:text-navy"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Kitchen Name" k="name" v={form.name} set={set} t="kitchen-name" cls="sm:col-span-2" />
            <F label="Full Address" k="address" v={form.address} set={set} t="kitchen-address" cls="sm:col-span-2" />
            <F label="Pincode" k="pincode" v={form.pincode} set={set} t="kitchen-pincode" />
            <F label="Phone" k="phone" v={form.phone} set={set} t="kitchen-phone" />
            <F label="Pickup Hours" k="pickup_hours" v={form.pickup_hours} set={set} t="kitchen-hours" />
            <F label="Estimated Ready Time" k="ready_time" v={form.ready_time} set={set} t="kitchen-ready" />
            <F label="Minimum Order (₹)" k="min_order" v={form.min_order} set={set} t="kitchen-minorder" type="number" />
            <div className="flex items-center gap-3 mt-6">
              <span className="text-sm text-navy font-medium">Active</span>
              <button type="button" role="switch" aria-checked={form.active} onClick={() => set("active", !form.active)} data-testid="kitchen-active-toggle"
                className={`relative w-11 h-6 rounded-full transition-colors ${form.active ? "bg-green-500" : "bg-black/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.active ? "translate-x-5" : ""}`} />
              </button>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-kitchen-btn" className="mt-5 flex items-center gap-2 bg-navy text-cream font-semibold px-6 py-2.5 rounded-full hover:bg-navy-soft disabled:opacity-60"><Save size={16} /> {saving ? "Saving..." : editingId ? "Update Kitchen" : "Save Kitchen"}</button>
        </div>
      )}

      <div className="space-y-3">
        {kitchens.length === 0 && <div className="text-center py-14 text-muted-foreground bg-white rounded-xl border border-black/5">No kitchens yet. Add your first operating kitchen.</div>}
        {kitchens.map((k) => (
          <div key={k.id} className="bg-white rounded-xl border border-black/5 p-5 flex justify-between items-start gap-4" data-testid={`kitchen-row-${k.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-navy">{k.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${k.active ? "bg-green-100 text-green-700" : "bg-black/10 text-muted-foreground"}`} data-testid={`kitchen-status-${k.id}`}>{k.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1 flex items-start gap-1.5"><MapPin size={13} className="text-gold mt-0.5 shrink-0" /> {k.address}{k.pincode ? ` - ${k.pincode}` : ""}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-navy">
                <span className="flex items-center gap-1"><Clock size={12} className="text-gold" /> Ready in {k.ready_time}</span>
                <span className="flex items-center gap-1"><Clock size={12} className="text-gold" /> {k.pickup_hours}</span>
                <span className="text-muted-foreground">Min order {rupee(k.min_order)}</span>
                {k.phone && <span className="text-muted-foreground">{k.phone}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <button onClick={() => toggleActive(k)} data-testid={`toggle-kitchen-${k.id}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${k.active ? "border-black/15 text-muted-foreground hover:border-navy/40" : "border-green-500 text-green-700"}`}>{k.active ? "Deactivate" : "Activate"}</button>
              <div className="flex gap-2">
                <button onClick={() => edit(k)} data-testid={`edit-kitchen-${k.id}`} className="text-muted-foreground hover:text-navy"><Pencil size={16} /></button>
                <button onClick={() => remove(k.id)} data-testid={`delete-kitchen-${k.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, k, v, set, t, cls = "", type = "text" }) {
  return (
    <div className={cls}>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={t} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );
}
